import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import '../job-board.css';
import { jbApi } from '../api';
import { useJBAuth } from '../auth';
import EmployerGuard from '../components/EmployerGuard';

const STATUS_OPTS = ['all','active','pending_payment','pending_approval','paused','closed','expired'];
const STATUS_COLOR = {
  active:           '#166534', pending_payment:'#854d0e', pending_approval:'#9a3412',
  paused:           '#475569', closed:'#64748b', expired:'#991b1b', rejected:'#991b1b', draft:'#64748b',
};
const STATUS_BG = {
  active:'#f0fdf4', pending_payment:'#fefce8', pending_approval:'#fff7ed',
  paused:'#f1f5f9', closed:'#f8fafc', expired:'#fef2f2', rejected:'#fef2f2', draft:'#f8fafc',
};

function JobsContent({ auth }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = auth;

  const [jobs, setJobs]           = useState([]);
  const [filter, setFilter]       = useState('all');
  const [loading, setLoading]     = useState(true);
  const [paymentMsg, setPaymentMsg] = useState('');

  useEffect(() => {
    if (!token) return;
    const params = filter !== 'all' ? { status: filter } : {};
    jbApi.employerJobs(token, params)
      .then(d => setJobs(d.jobs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, filter]);

  // Handle return from Stripe
  useEffect(() => {
    const sid = searchParams.get('session_id');
    const pay = searchParams.get('payment');
    if (!sid || pay !== 'success') return;
    jbApi.pollPayment(sid).then(d => {
      if (d.payment_status === 'paid') {
        setPaymentMsg('Payment confirmed! Your listing is now active.');
        jbApi.employerJobs(token).then(r => setJobs(r.jobs || []));
      }
    }).catch(() => {});
  }, [searchParams, token]);

  async function handleStatusChange(jobId, newStatus) {
    try {
      await jbApi.updateJob(jobId, { status: newStatus }, token);
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: newStatus } : j));
    } catch {}
  }

  async function handleDelete(jobId) {
    if (!window.confirm('Delete this listing? This cannot be undone.')) return;
    try {
      await jbApi.deleteJob(jobId, token);
      setJobs(prev => prev.filter(j => j.id !== jobId));
    } catch {}
  }

  const displayed = filter === 'all' ? jobs : jobs.filter(j => j.status === filter);

  return (
    <div className="jb-page" data-testid="employer-jobs-page">
      {/* Header */}
      <div style={{ background:'#0D2137', padding:'16px 0' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', padding:'0 24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <Link to="/employer/dashboard" style={{ fontSize:11, color:'rgba(245,240,232,.6)', textDecoration:'none' }}>← Dashboard</Link>
            <div style={{ fontSize:16, fontWeight:700, color:'#F5F0E8', marginTop:2 }}>My Listings</div>
          </div>
          <Link to="/career-hub/post" data-testid="post-job-cta" style={{
            background:'#C9A84C', color:'#0D2137', padding:'8px 18px',
            borderRadius:8, fontSize:13, fontWeight:800, textDecoration:'none',
          }}>+ Post New Job</Link>
        </div>
      </div>

      <div style={{ maxWidth:1100, margin:'0 auto', padding:'24px 24px 60px' }}>

        {paymentMsg && (
          <div style={{ background:'#f0fdf4', border:'1px solid #86efac', borderRadius:10, padding:'12px 18px', fontSize:13, fontWeight:600, color:'#166534', marginBottom:20 }} data-testid="payment-success-msg">
            ✓ {paymentMsg}
          </div>
        )}

        {/* Status Filter Tabs */}
        <div style={{ display:'flex', gap:4, marginBottom:20, flexWrap:'wrap' }}>
          {STATUS_OPTS.map(s => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`} style={{
              padding:'6px 14px', borderRadius:999, fontSize:12, fontWeight:600, cursor:'pointer',
              border: filter === s ? '1.5px solid #1B6B72' : '1.5px solid #ddd8cc',
              background: filter === s ? 'rgba(27,107,114,.08)' : '#fff',
              color: filter === s ? '#1B6B72' : '#5a6373',
              fontFamily:'Inter,sans-serif',
            }}>
              {s === 'all' ? 'All' : s.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase())}
            </button>
          ))}
        </div>

        {loading && <div style={{ textAlign:'center', padding:40, color:'#5a6373' }}>Loading listings…</div>}

        {!loading && displayed.length === 0 && (
          <div style={{ background:'#fff', borderRadius:10, padding:'40px 20px', textAlign:'center', boxShadow:'0 2px 8px rgba(13,33,55,.07)' }}>
            <div style={{ fontSize:13, color:'#5a6373', marginBottom:14 }}>No listings in this category yet.</div>
            <Link to="/career-hub/post" style={{ background:'#1B6B72', color:'#fff', padding:'10px 20px', borderRadius:8, fontSize:13, fontWeight:700, textDecoration:'none' }}>Post Your First Job →</Link>
          </div>
        )}

        {displayed.map(job => (
          <div key={job.id} data-testid={`job-row-${job.id}`} style={{ background:'#fff', borderRadius:10, padding:'16px 20px', marginBottom:12, boxShadow:'0 2px 8px rgba(13,33,55,.07)', display:'flex', alignItems:'center', gap:16 }}>
            {/* Logo */}
            <div style={{ width:44, height:44, borderRadius:10, background:'#1B6B72', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, color:'#fff', flexShrink:0 }}>
              {(job.logo_initial || job.title.slice(0,2)).toUpperCase()}
            </div>

            {/* Info */}
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:15, fontWeight:700, color:'#0D2137', marginBottom:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{job.title}</div>
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                <span style={{ background: STATUS_BG[job.status] || '#f8fafc', color: STATUS_COLOR[job.status] || '#64748b', padding:'2px 9px', borderRadius:999, fontSize:11, fontWeight:700 }}>
                  {(job.status || '').replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase())}
                </span>
                <span style={{ fontSize:11, color:'#5a6373' }}>{job.location} · {job.work_type?.replace(/_/g,' ')}</span>
                <span style={{ fontSize:11, color:'#5a6373' }}>{job.view_count || 0} views · {job.application_count || 0} apps</span>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display:'flex', gap:8, flexShrink:0 }}>
              {job.status === 'active' && (
                <button onClick={() => handleStatusChange(job.id, 'paused')} data-testid={`pause-${job.id}`} style={{ padding:'6px 12px', borderRadius:7, border:'1.5px solid #ddd8cc', background:'#fff', fontSize:12, fontWeight:600, cursor:'pointer', color:'#475569', fontFamily:'Inter,sans-serif' }}>
                  Pause
                </button>
              )}
              {job.status === 'paused' && (
                <button onClick={() => handleStatusChange(job.id, 'active')} data-testid={`resume-${job.id}`} style={{ padding:'6px 12px', borderRadius:7, border:'1.5px solid #1B6B72', background:'rgba(27,107,114,.06)', fontSize:12, fontWeight:600, cursor:'pointer', color:'#1B6B72', fontFamily:'Inter,sans-serif' }}>
                  Resume
                </button>
              )}
              {!['closed','expired','rejected'].includes(job.status) && (
                <button onClick={() => handleStatusChange(job.id, 'closed')} data-testid={`close-${job.id}`} style={{ padding:'6px 12px', borderRadius:7, border:'1.5px solid #ddd8cc', background:'#fff', fontSize:12, fontWeight:600, cursor:'pointer', color:'#64748b', fontFamily:'Inter,sans-serif' }}>
                  Close
                </button>
              )}
              <Link to={`/employer/jobs/${job.id}/applicants`} data-testid={`applicants-${job.id}`} style={{ padding:'6px 12px', borderRadius:7, border:'1.5px solid #1B6B72', background:'rgba(27,107,114,.06)', fontSize:12, fontWeight:600, color:'#1B6B72', textDecoration:'none', whiteSpace:'nowrap' }}>
                Applicants ({job.application_count || 0})
              </Link>
              <button onClick={() => handleDelete(job.id)} data-testid={`delete-${job.id}`} style={{ padding:'6px 10px', borderRadius:7, border:'1.5px solid #fca5a5', background:'#fef2f2', fontSize:12, cursor:'pointer', color:'#991b1b', fontFamily:'Inter,sans-serif', fontWeight:600 }}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function EmployerJobsPage() {
  const auth = useJBAuth();
  return <EmployerGuard><JobsContent auth={auth} /></EmployerGuard>;
}
